import type { Asset, AudioClip, Composition, Overlay, Scene, Segment } from '@shared/types'
import { FRAME_SEC } from './time'

export interface TimedSegment {
  segment: Segment
  index: number
  startSec: number // position in the composited output timeline
  endSec: number
}

/** Segment output duration: source range compressed/expanded by speed. */
export function segmentOutputDuration(seg: Segment): number {
  return Math.max(0, (seg.outSec - seg.inSec) / seg.speed)
}

/** Cumulative placement of every Segment on the composited output timeline. */
export function getSegmentTimeline(segments: Segment[]): TimedSegment[] {
  let t = 0
  return segments.map((segment, index) => {
    const dur = segmentOutputDuration(segment)
    const item: TimedSegment = { segment, index, startSec: t, endSec: t + dur }
    t += dur
    return item
  })
}

export function findSegmentAt(timeline: TimedSegment[], sec: number): TimedSegment | null {
  if (timeline.length === 0) return null
  for (const item of timeline) {
    if (sec >= item.startSec && sec < item.endSec) return item
  }
  // clamp to last if sec is at/after the very end
  return sec >= timeline[timeline.length - 1].endSec ? timeline[timeline.length - 1] : timeline[0]
}

export function getVideoDurationSec(composition: Composition): number {
  const timeline = getSegmentTimeline(composition.tracks.video)
  return timeline.length ? timeline[timeline.length - 1].endSec : 0
}

export function getOverlayEndSec(o: Overlay): number {
  if (o.kind === 'logo') return Infinity
  return o.startSec + o.durationSec
}

export function getAudioEndSec(a: AudioClip, fallbackTotal: number): number {
  if (a.outSec == null) return fallbackTotal
  return a.startSec + (a.outSec - a.inSec)
}

/** Total composited duration = latest of video / overlays / audio ends. */
export function getTotalDurationSec(composition: Composition): number {
  const videoEnd = getVideoDurationSec(composition)
  const overlayEnd = composition.tracks.overlays.reduce((max, o) => {
    const end = getOverlayEndSec(o)
    return Number.isFinite(end) ? Math.max(max, end) : max
  }, 0)
  const audioEnd = composition.tracks.audio.reduce(
    (max, a) => Math.max(max, getAudioEndSec(a, videoEnd)),
    0,
  )
  return Math.max(videoEnd, overlayEnd, audioEnd)
}

export function activeOverlaysAt(overlays: Overlay[], sec: number): Overlay[] {
  return overlays.filter((o) => {
    if (o.kind === 'logo') return true
    return sec >= o.startSec && sec < o.startSec + o.durationSec
  })
}

// ==========================================================================
// UI MK2 第1段 1a — ズーム / ルーラー / 吸着 / 分割 の純関数群
// ==========================================================================

// ---------- zoom ----------
export const MIN_PX_PER_SEC = 4
export const MAX_PX_PER_SEC = 400
export const DEFAULT_PX_PER_SEC = 50
/** ⌘+/⌘−・ホイールズーム 1 段あたりの倍率 */
export const ZOOM_STEP_FACTOR = 1.4

export function clampPxPerSec(v: number): number {
  return Math.min(MAX_PX_PER_SEC, Math.max(MIN_PX_PER_SEC, v))
}

export function secToPx(sec: number, pxPerSec: number): number {
  return sec * pxPerSec
}

export function pxToSec(px: number, pxPerSec: number): number {
  return px / pxPerSec
}

/** 「全体を表示」: totalSec がちょうど containerWidthPx に収まる px/sec。 */
export function fitPxPerSec(totalSec: number, containerWidthPx: number): number {
  if (totalSec <= 0 || containerWidthPx <= 0) return DEFAULT_PX_PER_SEC
  return clampPxPerSec(containerWidthPx / totalSec)
}

/**
 * 30s → 10s → 5s → 1s → 0.5s → 0.2s → フレーム、細かい方から見て最初に
 * ラベル間隔が確保できる間隔を採用。UI_MK2_PHASE2.md §2-7 (reviewer minor):
 * 5s が最粗だと「全体を表示」で尺の長い Composition を大きくズームアウトした
 * 時に目盛が詰まりすぎてラベルが重なる — 10s/30s を追加。
 */
const TICK_CANDIDATES_ASC = [FRAME_SEC, 0.2, 0.5, 1, 5, 10, 30] as const

export function chooseTickIntervalSec(pxPerSec: number, minSpacingPx = 40): number {
  for (const c of TICK_CANDIDATES_ASC) {
    if (c * pxPerSec >= minSpacingPx) return c
  }
  return TICK_CANDIDATES_ASC[TICK_CANDIDATES_ASC.length - 1]
}

// ---------- snapping ----------
export interface SnapResult {
  sec: number
  snappedTo: number | null
}

/** `sec` を candidates のうち toleranceSec 以内で最も近いものへ吸着する。無ければそのまま。 */
export function snapSec(sec: number, candidates: number[], toleranceSec: number): SnapResult {
  let best: number | null = null
  let bestDist = Infinity
  for (const c of candidates) {
    const d = Math.abs(c - sec)
    if (d <= toleranceSec && d < bestDist) {
      best = c
      bestDist = d
    }
  }
  return best == null ? { sec, snappedTo: null } : { sec: best, snappedTo: best }
}

/** 吸着候補: 再生ヘッド + 他 Segment の境界 + 他 Overlay の端（logo は対象外）。 */
export function collectSnapTargets(
  timeline: TimedSegment[],
  overlays: Overlay[],
  playheadSec: number,
  opts?: { excludeSegmentId?: string; excludeOverlayId?: string },
): number[] {
  const out = new Set<number>()
  out.add(playheadSec)
  for (const t of timeline) {
    if (t.segment.id === opts?.excludeSegmentId) continue
    out.add(t.startSec)
    out.add(t.endSec)
  }
  for (const o of overlays) {
    if (o.kind === 'logo' || o.id === opts?.excludeOverlayId) continue
    out.add(o.startSec)
    out.add(o.startSec + o.durationSec)
  }
  return Array.from(out)
}

/**
 * reviewer 差し戻し B1: Segment トリム専用の吸着候補。
 *
 * バグの原因: 右トリム中に毎 pointermove で `collectSnapTargets` を「今動かして
 * いる最中」のライブな Composition から取り直すと、次 Segment の startSec は
 * （Segment が隙間なく連続配置される設計上）常に「いま動かしている端そのもの」
 * と一致してしまい、距離 0 で自分自身に吸着し続けて 8px（許容量）刻みでしか
 * 動かせなくなる。
 *
 * 直し: (a) 自分（`segmentIndex`）と、それより後ろの index の Segment 境界は
 * 対象外にする — この trim で自分の duration が変わると後続 Segment の位置は
 * すべてズレるため、固定候補として持っていても実際の新しい境界とは合わなくなる
 * （後ろの Segment は根本的に「動くドラッグ中は信頼できる候補ではない」）。
 * (b) ドラッグ開始時点の自分の端の値（`excludeCoincidentSec`）と一致する候補
 * （前 Segment の end が自分の startSec と一致する・同座標の Overlay 端 等）も
 * 除外し、「開始位置そのものに吸着して動き出せない」事故を防ぐ。
 *
 * 呼び出し側は `beginResize`（ドラッグ開始）時点で一度だけこれを計算して
 * 固定し、pointermove の間はその凍結済みリストを使い回すこと（ライブな
 * Composition から毎フレーム取り直さない）。
 */
export function collectSegmentTrimSnapTargets(
  timeline: TimedSegment[],
  overlays: Overlay[],
  playheadSec: number,
  opts: { segmentIndex: number; excludeCoincidentSec: number },
): number[] {
  const out = new Set<number>()
  out.add(playheadSec)
  for (const t of timeline) {
    if (t.index >= opts.segmentIndex) continue // 自分・自分より後ろは対象外
    out.add(t.startSec)
    out.add(t.endSec)
  }
  for (const o of overlays) {
    if (o.kind === 'logo') continue
    out.add(o.startSec)
    out.add(o.startSec + o.durationSec)
  }
  const EPS = 1e-6
  return Array.from(out).filter((v) => Math.abs(v - opts.excludeCoincidentSec) > EPS)
}

// ---------- overlay left trim (UI MK2 第2段 2a-5) ----------
export interface OverlayLeftTrimResult {
  startSec: number
  durationSec: number
}

/**
 * task spec §2-5: Overlay 左端ドラッグ。end（= 元の startSec + durationSec）を
 * 固定したまま、新しい startSec を吸着済みの `rawStartSec` から計算する。
 * 0 未満にはならず、durationSec が `minDurationSec`（既定 0.2s）を割らないよう
 * 上限側もクランプする。
 */
export function computeOverlayLeftTrim(
  fixedEndSec: number,
  rawStartSec: number,
  minDurationSec = 0.2,
): OverlayLeftTrimResult {
  const startSec = Math.min(fixedEndSec - minDurationSec, Math.max(0, rawStartSec))
  return { startSec, durationSec: fixedEndSec - startSec }
}

// ---------- split ----------
export interface SplitPoint {
  /** 左側（元の Segment のまま残す側）の新しい outSec（元素材の時間軸） */
  leftOutSec: number
  /** 右側（新規複製する側）の新しい inSec（元素材の時間軸） */
  rightInSec: number
}

/**
 * atSec（合成後タイムライン上の秒）で timed を割った時の左右の source in/out。
 * 端 ±frameSec 以内は分割対象なし（null）。
 */
export function computeSplit(timed: TimedSegment, atSec: number, frameSec = FRAME_SEC): SplitPoint | null {
  if (atSec <= timed.startSec + frameSec || atSec >= timed.endSec - frameSec) return null
  const seg = timed.segment
  const splitSourceSec = seg.inSec + (atSec - timed.startSec) * seg.speed
  return { leftOutSec: splitSourceSec, rightInSec: splitSourceSec }
}

/**
 * reviewer 差し戻し B2: ⌘B で作る左右 2 つの Segment。右側は常に新規カット点
 * なので `transitionIn` は必ず 'cut'（元が 'fade' でも継承しない — 継承すると
 * 連続ショットの途中に意図しないフェードが入り書き出しが破綻する）。左側は
 * 元の Segment の transitionIn をそのまま引き継ぐ。
 */
export function buildSplitSegments(original: Segment, split: SplitPoint, rightId: string): { left: Segment; right: Segment } {
  return {
    left: { ...original, outSec: split.leftOutSec },
    right: { ...original, id: rightId, inSec: split.rightInSec, transitionIn: 'cut' },
  }
}

/**
 * reviewer 差し戻し m4: ⌘B/Q/W の対象 Segment 解決。選択中の Segment があっても
 * 再生ヘッドがその範囲外なら、CapCut と同様「再生ヘッド下の Segment」へフォール
 * バックする（今までは選択があると常にそれを使い、範囲外だと問答無用で「端では
 * 分割できません」になっていた）。
 */
export function resolveSplitTarget(
  timeline: TimedSegment[],
  selectedSegmentId: string | undefined,
  atSec: number,
): TimedSegment | null {
  const selected = selectedSegmentId ? timeline.find((t) => t.segment.id === selectedSegmentId) : undefined
  if (selected && atSec >= selected.startSec && atSec < selected.endSec) return selected
  return findSegmentAt(timeline, atSec)
}

// ---------- overlay lanes ----------
export type OverlayLaneKey = 'card' | 'label' | 'logo' | 'text'

export const OVERLAY_KIND_TO_LANE: Record<Overlay['kind'], OverlayLaneKey> = {
  title: 'card',
  end: 'card',
  label: 'label',
  logo: 'logo',
  free: 'text',
}

export function overlaysForLane(overlays: Overlay[], lane: OverlayLaneKey): Overlay[] {
  return overlays.filter((o) => OVERLAY_KIND_TO_LANE[o.kind] === lane)
}

// ---------- segment thumbnails ----------
/**
 * Segment 左端に出す scene サムネの元。`segment.inSec` を含む scene があればそれ
 * （トリム後の実際の始点に一番近い絵）、無ければ asset の先頭 scene にフォールバック。
 */
export function findSceneForSegment(asset: Asset | undefined, segment: Segment): Scene | null {
  if (!asset) return null
  const hit = asset.scenes.find((sc) => segment.inSec >= sc.startSec && segment.inSec < sc.endSec)
  return hit ?? asset.scenes[0] ?? null
}

// ---------- selection pruning (Undo/Redo) ----------
/** editorStore.SelectionItem と同じ形。循環 import を避けるためここで独立定義する。 */
export interface SelectionRef {
  kind: 'segment' | 'overlay' | 'audio'
  id: string
}

/**
 * reviewer 差し戻し M1: Undo/Redo で Composition が切り替わった直後、既に
 * 存在しない id を選択に残さない（右ペインが空白の Segment インスペクタの
 * まま固まる事故の防止）。
 */
export function pruneSelection<T extends SelectionRef>(comp: Composition, selection: T[]): T[] {
  return selection.filter((item) => {
    if (item.kind === 'segment') return comp.tracks.video.some((s) => s.id === item.id)
    if (item.kind === 'overlay') return comp.tracks.overlays.some((o) => o.id === item.id)
    return comp.tracks.audio.some((a) => a.id === item.id)
  })
}
